import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  TablePagination,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import {
  Search,
  Clear,
  Receipt,
  DateRange,
  ShoppingCart,
  Payment,
  Event,
  Business,
  OpenInNew,
} from '@mui/icons-material';
import debounce from 'lodash.debounce';

// Styled table container with subtle shadow
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  background: '#ffffff',
}));

// Styled button for PDF and Excel download
const DownloadButton = styled(Button)(({ theme }) => ({
  minWidth: '40px', // Compact size for icon-only button
  padding: theme.spacing(0.5), // Reduced padding
  color: '#21ccbb',
  '&:hover': {
    backgroundColor: 'rgba(33, 204, 187, 0.1)',
  },
  '& img': {
    width: 24, // Adjust size as needed
    height: 24,
  },
}));

// Styled search input
const SearchInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiInputBase-root': {
    background: '#fff',
    borderRadius: 8,
    paddingRight: theme.spacing(1),
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5),
  },
}));

// Styled TableHead cell for consistent alignment
const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: '#1e293b',
  padding: theme.spacing(1, 2),
  textAlign: 'center',
  whiteSpace: 'nowrap',
  '& > div': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& svg': {
    marginRight: theme.spacing(0.5),
    fontSize: 18,
  },
}));

const FacturesSection = ({ restaurantId }) => {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Debounced search function
  const debouncedFetchFactures = useCallback(
    debounce(async (query, pageNum, limit) => {
      try {
        setSearchLoading(true);
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }
        const response = await axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/factures`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            search: query,
            page: pageNum + 1,
            limit,
          },
        });
        setFactures(response.data.factures);
        setTotalCount(response.data.pagination.total);
        setError(null);
      } catch (err) {
        setError(err.message || 'Erreur lors de la récupération des factures');
        console.error('Error fetching factures:', err);
      } finally {
        setSearchLoading(false);
        setLoading(false);
      }
    }, 500),
    [restaurantId]
  );

  // Trigger search on query, page, or rowsPerPage change
  useEffect(() => {
    debouncedFetchFactures(searchQuery, page, rowsPerPage);
    return () => debouncedFetchFactures.cancel();
  }, [searchQuery, page, rowsPerPage, debouncedFetchFactures]);

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && !searchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress size={40} sx={{ color: '#21ccbb' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, fontFamily: "'Inter', sans-serif" }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}
      >
        Factures
      </Typography>
      <SearchInput
        fullWidth
        variant="outlined"
        placeholder="Rechercher par date, période ou montant..."
        value={searchQuery}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: '#21ccbb' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {searchLoading ? (
                <CircularProgress size={20} sx={{ color: '#21ccbb' }} />
              ) : (
                searchQuery && (
                  <IconButton onClick={handleClearSearch} size="small">
                    <Clear sx={{ color: '#21ccbb' }} />
                  </IconButton>
                )
              )}
            </InputAdornment>
          ),
        }}
      />
      {factures.length === 0 ? (
        <Typography color="textSecondary">Aucune facture disponible.</Typography>
      ) : (
        <>
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell>
                    <div>
                      <Receipt sx={{ color: '#21ccbb' }} />
                      Numéro Facture
                    </div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div>
                      <DateRange sx={{ color: '#21ccbb' }} />
                      Période
                    </div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div>
                      <ShoppingCart sx={{ color: '#21ccbb' }} />
                      Montant Total Commandes
                    </div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div>
                      <Payment sx={{ color: '#21ccbb' }} />
                      Montant à Payer
                    </div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div>
                      <Event sx={{ color: '#21ccbb' }} />
                      Date Création
                    </div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div>
                      <Business sx={{ color: '#21ccbb' }} />
                      ICE
                    </div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div>
                      <OpenInNew sx={{ color: '#21ccbb' }} />
                      Action
                    </div>
                  </StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {factures.map((facture) => (
                  <TableRow key={facture.id}>
                    <TableCell>{facture.id}</TableCell>
                    <TableCell>
                      {facture.debut_periode} - {facture.fin_periode}
                    </TableCell>
                    <TableCell>{parseFloat(facture.montant_total_commandes).toFixed(2)} MAD</TableCell>
                    <TableCell>{parseFloat(facture.montant_a_paye).toFixed(2)} MAD</TableCell>
                    <TableCell>{new Date(facture.created_at).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell>{facture.ice || 'N/A'}</TableCell>
                    <TableCell sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <DownloadButton href={facture.pdf_url} target="_blank">
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/4726/4726010.png"
                          alt="PDF Logo"
                          style={{ width: 24, height: 24 }}
                        />
                      </DownloadButton>
                      <DownloadButton href={facture.excel_url} target="_blank">
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/732/732220.png"
                          alt="Excel Logo"
                          style={{ width: 24, height: 24 }}
                        />
                      </DownloadButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page :"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default FacturesSection;